import React, { useState } from "react";
import "./JobApplicationForm.css";

export default function JobApplicationForm({ job, onClose }) {
  const [formData, setFormData] = useState({
    full_name: "",
    email: "",
    phone_number: "",
    alternate_phone: "",
    current_location: "",
    date_of_birth: "",
    highest_qualification: "",
    year_of_passing: "",
    total_experience: "",
    current_designation: "",
    current_company: "",
    expected_salary: "",
  });

  const [resumeFile, setResumeFile] = useState(null);
  const [resumeFileName, setResumeFileName] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type (PDF, DOC, DOCX)
      const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (!allowedTypes.includes(file.type)) {
        alert('Please upload a PDF or Word document (.pdf, .doc, .docx)');
        e.target.value = '';
        return;
      }
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('File size must be less than 5MB');
        e.target.value = '';
        return;
      }
      setResumeFile(file);
      setResumeFileName(file.name);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!resumeFile) {
      alert('Please upload your CV/Resume');
      return;
    }

    setLoading(true);

    try {
      // Create FormData for multipart upload
      const uploadFormData = new FormData();
      
      // Add form fields
      Object.keys(formData).forEach(key => {
        uploadFormData.append(key, formData[key]);
      });
      
      // Add job_id
      uploadFormData.append('job_id', job.id);
      
      // Add resume file
      uploadFormData.append('resume', resumeFile);
      
      // Send application to backend
      const response = await fetch("/backend/api/public/applications.php", {
        method: "POST",
        body: uploadFormData,
      });

      const data = await response.json();

      if (!response.ok) throw new Error(data.error || "Failed to submit application");

      setSubmitted(true);
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (error) {
      alert("Error submitting application: " + error.message);
      setLoading(false);
    }
  };

  return (
    <div className="job-app-modal-overlay" onClick={onClose}>
      <div className="job-app-modal" onClick={(e) => e.stopPropagation()}>
        {/* Close Button */}
        <button className="job-app-close" onClick={onClose}>
          ✕
        </button>

        {/* Hero Section */}
        <div className="job-app-hero">
          <div className="job-app-hero-bg">
            <div className="job-app-hero-overlay"></div>
            <div className="job-app-hero-shapes">
              <div className="job-app-shape job-app-shape-1"></div>
              <div className="job-app-shape job-app-shape-2"></div>
            </div>
          </div>

          <div className="job-app-hero-content">
            {job.logo_path && (
              <img
                src={`${window.location.origin}${job.logo_path}`}
                alt="company"
                className="job-app-hero-logo"
              />
            )}
            <h2 className="job-app-hero-title">{job.title}</h2>
            <div className="job-app-hero-badges">
              <span className="job-app-badge">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 22s7-7.58 7-12.5A7 7 0 0 0 5 9.5C5 14.42 12 22 12 22Z" />
                </svg>
                {job.office_location}
              </span>
              <span className="job-app-badge">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="5" width="18" height="16" rx="2" />
                  <path d="M3 9H21M8 3V6M16 3V6" />
                </svg>
                Deadline: {job.last_date}
              </span>
            </div>
          </div>
        </div>

        {/* Form Content */}
        <div className="job-app-content">
          {submitted ? (
            <div className="job-app-success">
              <div className="job-app-success-icon">
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <polyline points="22 4 12 14.01 9 11.01" />
                </svg>
              </div>
              <h3 className="job-app-success-title">Application Submitted!</h3>
              <p className="job-app-success-desc">Thank you for applying. We'll review your application and get back to you soon.</p>
            </div>
          ) : (
            <>
              <div className="job-app-header">
                <h3 className="job-app-form-title">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                    <circle cx="8.5" cy="7" r="4" />
                    <path d="M20 8v6m3-3h-6" />
                  </svg>
                  Apply for this Job
                </h3>
                <p className="job-app-form-desc">Fill in the details below to submit your application.</p>
              </div>

              <form onSubmit={handleSubmit} className="job-app-form">
                <div className="job-app-form-grid">
                  {/* Full Name */}
                  <div className="job-app-form-group">
                    <label htmlFor="full_name" className="job-app-label">
                      Full Name <span className="required">*</span>
                    </label>
                    <div className="job-app-input-wrapper">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                        <circle cx="12" cy="7" r="4" />
                      </svg>
                      <input
                        type="text"
                        id="full_name"
                        name="full_name"
                        placeholder="Enter your full name"
                        value={formData.full_name}
                        onChange={handleChange}
                        required
                        className="job-app-input"
                      />
                    </div>
                  </div>

                  {/* Email */}
                  <div className="job-app-form-group">
                    <label htmlFor="email" className="job-app-label">
                      Email Address <span className="required">*</span>
                    </label>
                    <div className="job-app-input-wrapper">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="2" y="4" width="20" height="16" rx="2" />
                        <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                      </svg>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        placeholder="your.email@example.com"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="job-app-input"
                      />
                    </div>
                  </div>

                  {/* Phone Number */}
                  <div className="job-app-form-group">
                    <label htmlFor="phone_number" className="job-app-label">
                      Phone Number <span className="required">*</span>
                    </label>
                    <div className="job-app-input-wrapper">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                      </svg>
                      <input
                        type="tel"
                        id="phone_number"
                        name="phone_number"
                        placeholder="+91 XXXXX XXXXX"
                        value={formData.phone_number}
                        onChange={handleChange}
                        required
                        className="job-app-input"
                      />
                    </div>
                  </div>

                  {/* Alternate Phone */}
                  <div className="job-app-form-group">
                    <label htmlFor="alternate_phone" className="job-app-label">
                      Alternate Phone Number
                    </label>
                    <div className="job-app-input-wrapper">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                      </svg>
                      <input
                        type="tel"
                        id="alternate_phone"
                        name="alternate_phone"
                        placeholder="+91 XXXXX XXXXX"
                        value={formData.alternate_phone}
                        onChange={handleChange}
                        className="job-app-input"
                      />
                    </div>
                  </div>

                  {/* Current Location */}
                  <div className="job-app-form-group">
                    <label htmlFor="current_location" className="job-app-label">
                      Current Location <span className="required">*</span>
                    </label>
                    <div className="job-app-input-wrapper">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                        <circle cx="12" cy="10" r="3" />
                      </svg>
                      <input
                        type="text"
                        id="current_location"
                        name="current_location"
                        placeholder="City, State"
                        value={formData.current_location}
                        onChange={handleChange}
                        required
                        className="job-app-input"
                      />
                    </div>
                  </div>

                  {/* Date of Birth */}
                  <div className="job-app-form-group">
                    <label htmlFor="date_of_birth" className="job-app-label">
                      Date of Birth <span className="required">*</span>
                    </label>
                    <div className="job-app-input-wrapper">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                        <line x1="16" y1="2" x2="16" y2="6" />
                        <line x1="8" y1="2" x2="8" y2="6" />
                        <line x1="3" y1="10" x2="21" y2="10" />
                      </svg>
                      <input
                        type="date"
                        id="date_of_birth"
                        name="date_of_birth"
                        value={formData.date_of_birth}
                        onChange={handleChange}
                        required
                        className="job-app-input"
                      />
                    </div>
                  </div>

                  {/* Highest Qualification */}
                  <div className="job-app-form-group">
                    <label htmlFor="highest_qualification" className="job-app-label">
                      Highest Qualification <span className="required">*</span>
                    </label>
                    <div className="job-app-input-wrapper">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                      </svg>
                      <select
                        id="highest_qualification"
                        name="highest_qualification"
                        value={formData.highest_qualification}
                        onChange={handleChange}
                        required
                        className="job-app-input"
                      >
                        <option value="">Select qualification</option>
                        <option value="12th Pass">12th Pass</option>
                        <option value="Bachelor's Degree">Bachelor's Degree</option>
                        <option value="Master's Degree">Master's Degree</option>
                        <option value="MBA">MBA</option>
                        <option value="B.Tech">B.Tech</option>
                        <option value="M.Tech">M.Tech</option>
                        <option value="Diploma">Diploma</option>
                      </select>
                    </div>
                  </div>

                  {/* Year of Passing */}
                  <div className="job-app-form-group">
                    <label htmlFor="year_of_passing" className="job-app-label">
                      Year of Passing <span className="required">*</span>
                    </label>
                    <div className="job-app-input-wrapper">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10" />
                        <polyline points="12 6 12 12 16 14" />
                      </svg>
                      <input
                        type="number"
                        id="year_of_passing"
                        name="year_of_passing"
                        placeholder="YYYY"
                        value={formData.year_of_passing}
                        onChange={handleChange}
                        required
                        min="1990"
                        max={new Date().getFullYear()}
                        className="job-app-input"
                      />
                    </div>
                  </div>

                  {/* Total Experience */}
                  <div className="job-app-form-group">
                    <label htmlFor="total_experience" className="job-app-label">
                      Total Experience (Years) <span className="required">*</span>
                    </label>
                    <div className="job-app-input-wrapper">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2zM22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
                      </svg>
                      <input
                        type="number"
                        id="total_experience"
                        name="total_experience"
                        placeholder="Years"
                        value={formData.total_experience}
                        onChange={handleChange}
                        required
                        min="0"
                        className="job-app-input"
                      />
                    </div>
                  </div>

                  {/* Current Designation */}
                  <div className="job-app-form-group">
                    <label htmlFor="current_designation" className="job-app-label">
                      Current Role/Designation
                    </label>
                    <div className="job-app-input-wrapper">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                        <circle cx="12" cy="7" r="4" />
                      </svg>
                      <input
                        type="text"
                        id="current_designation"
                        name="current_designation"
                        placeholder="Your current designation"
                        value={formData.current_designation}
                        onChange={handleChange}
                        className="job-app-input"
                      />
                    </div>
                  </div>

                  {/* Current Company */}
                  <div className="job-app-form-group">
                    <label htmlFor="current_company" className="job-app-label">
                      Current Company
                    </label>
                    <div className="job-app-input-wrapper">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
                        <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
                      </svg>
                      <input
                        type="text"
                        id="current_company"
                        name="current_company"
                        placeholder="Company name"
                        value={formData.current_company}
                        onChange={handleChange}
                        className="job-app-input"
                      />
                    </div>
                  </div>

                  {/* Expected Salary */}
                  <div className="job-app-form-group">
                    <label htmlFor="expected_salary" className="job-app-label">
                      Expected Salary (Annual) <span className="required">*</span>
                    </label>
                    <div className="job-app-input-wrapper">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="12" y1="1" x2="12" y2="23" />
                        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
                      </svg>
                      <input
                        type="number"
                        id="expected_salary"
                        name="expected_salary"
                        placeholder="Expected salary"
                        value={formData.expected_salary}
                        onChange={handleChange}
                        required
                        min="0"
                        className="job-app-input"
                      />
                    </div>
                  </div>

                  {/* Resume */}
                  <div className="job-app-form-group" style={{gridColumn: "1 / -1"}}>
                    <label htmlFor="resume" className="job-app-label">
                      Upload Your CV <span className="required">*</span>
                    </label>
                    <div className="job-app-file-upload">
                      <input
                        type="file"
                        id="resume"
                        name="resume"
                        accept=".pdf,.doc,.docx"
                        onChange={handleFileChange}
                        required
                        className="job-app-file-input"
                      />
                      <div className="job-app-file-label">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                          <polyline points="17 8 12 3 7 8" />
                          <line x1="12" y1="3" x2="12" y2="15" />
                        </svg>
                        <div className="file-upload-text">
                          {resumeFileName ? (
                            <>
                              <span className="file-name">✓ {resumeFileName}</span>
                              <span className="file-hint">Click to change file</span>
                            </>
                          ) : (
                            <>
                              <span className="file-text">Click to upload or drag and drop</span>
                              <span className="file-hint">PDF, DOC, or DOCX • Max 5MB</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Privacy Notice */}
                <div className="job-app-privacy">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                  </svg>
                  <p>Your information is safe with us and will not be shared with anyone.</p>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={loading}
                  className="job-app-submit-btn"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                  </svg>
                  {loading ? "Submitting..." : "Apply Now"}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
